import { Track } from "@/src/lib/tracking";
import { useCallback, useState } from "react";
import { OspToast } from "@ospweb/sdk-ui";

import { ROUTER_PATH } from "@config/router";
import { getPaymentCheckErrorMessage } from "@hooks/useAppPurchaseFlow";
import { usePayment } from "@hooks/usePayment";
import { sendBeginCheckoutGtm } from "@utils/member/purchaseGtm";
import { MemberProduct, PayChannel } from "@features/api/memberApi";

interface UseMemberStripeCheckoutParams {
  data?: MemberProduct[];
}

export const useMemberStripeCheckout = ({
  data,
}: UseMemberStripeCheckoutParams) => {
  const { handlePaymentCheck } = usePayment();
  const [isPaying, setIsPaying] = useState(false);

  const handleCheckout = useCallback(
    async (planId: string, payChannel: PayChannel) => {
      setIsPaying(true);
      const currentPlan = data?.find((item) => item.id === planId);
      const price = currentPlan?.price;

      const { data: checkoutUrl, error } = await handlePaymentCheck(planId, {
        payChannel,
      });
      const channelTrace =
        payChannel === PayChannel.PAYPAL ? "paypal" : "stripe";
      Track.log("info", "Member", `[trace][pay] ${channelTrace} result`, {
        arg1: String(checkoutUrl),
        arg2: String(error),
      });
      setIsPaying(false);

      if (error) {
        const errMsg = getPaymentCheckErrorMessage({
          code: error.code,
          msg: error.msg,
        });
        OspToast.error({
          content: errMsg,
        });
        return;
      }
      if (!checkoutUrl) {
        // API 成功但无支付链接（如 PayPal 下单失败返回 eligible=false）：
        // 不能裸跳转刷新页面，先提示失败（begin_checkout 也尚未上报）
        Track.log("error", "Member", "[trace][pay] empty checkout link", {
          arg1: planId,
          arg2: payChannel,
        });
        OspToast.error({
          content: getPaymentCheckErrorMessage({}),
        });
        return;
      }

      // item_id 用入参 planId 而非 currentPlan.id：产品列表没命中时 currentPlan 是 undefined，
      // 但我们仍然知道用户结的是哪个套餐，不能让这条事件退化成没有商品 id。
      sendBeginCheckoutGtm({
        id: planId,
        name: currentPlan?.name,
        price,
      });
      // 跳转前清理当前 history 条目，支付页点浏览器返回时会回到无参数的 /member
      window.history.replaceState({}, "", ROUTER_PATH.MEMBER);
      window.location.href = checkoutUrl;
    },
    [data, handlePaymentCheck],
  );

  const handleStripeClick = useCallback(
    (planId: string) => handleCheckout(planId, PayChannel.STRIPE),
    [handleCheckout],
  );

  const handlePaypalClick = useCallback(
    (planId: string) => handleCheckout(planId, PayChannel.PAYPAL),
    [handleCheckout],
  );

  return {
    isPaying,
    handleStripeClick,
    handlePaypalClick,
  };
};
